// ccAuthProvider.ts — JEDYNY dostawca tożsamości Control Center.
//
// ┌─ CO ZASTĘPUJE ──────────────────────────────────────────────────────────────────────────────┐
// │ `crmAuthProvider` i `driveAuthProvider` wysyłały z przeglądarki hasło operatora CRM oraz    │
// │ hasło administratora Drive. Dwa logowania, dwa hasła człowieka w ruchu sieciowym, dwie      │
// │ sesje do wygaszenia — i konsola, o której nie dało się powiedzieć, czy ktoś jest w niej     │
// │ zalogowany, bo odpowiedź brzmiała „w połowie".                                              │
// │                                                                                              │
// │ Teraz logowanie jest jedno i prowadzi do Control Center. Do źródeł chodzi backend własną    │
// │ tożsamością serwisową, więc hasła do CRM i Drive nie są nikomu w przeglądarce potrzebne.    │
// │                                                                                              │
// │ Kontrakt `AuthProvider` przewidział to wprost: „przejście na zunifikowaną tożsamość =       │
// │ zastąpienie kilku implementacji jedną". To jest ta jedna.                                    │
// └──────────────────────────────────────────────────────────────────────────────────────────────┘
import type { ApiClient } from '@/lib/api/client';
import { makePermissionSet } from './permissions';
import { permissionsForRoles, type LegacyRole } from './roleMap';
import type { AuthProvider, Credentials, Identity } from './types';

interface OdpowiedzTozsamosci {
  tozsamosc: { id: number; email: string; rola: string; miasto: string | null; restauracja_id: number | null };
}

/**
 * Rola Control Center → uprawnienia interfejsu.
 *
 * ADMIN dostaje sumę tego, co dotąd dawały dwie osobne sesje — bo dotąd administrator logował
 * się do obu systemów i widział jedno i drugie. Zmiana dotyczy sposobu logowania, nie zakresu
 * tego, co wolno oglądać.
 *
 * SUBADMIN i RESTAURACJA dostają WYŁĄCZNIE uprawnienia dostaw. To nie jest zaniżenie na wszelki
 * wypadek: backend odmawia tym rolom danych CRM, bo model zakresu CRM ich nie zna (patrz
 * `control-center/server/lib/montaz.js`). Gdyby interfejs pokazał im ekrany sprzedaży, każdy
 * z nich skończyłby się komunikatem odmowy — czyli obiecalibyśmy coś, czego nie ma.
 *
 * Rozjazd tej tabeli z rzeczywistą odmową backendu jest defektem w obie strony: pokazany ekran,
 * który zwróci 403, i ukryty ekran, który backend by wydał.
 */
const ROLA_NA_ROLE_ZRODEL: Record<string, readonly LegacyRole[]> = {
  admin: ['crm_admin', 'drive_admin'],
  subadmin: ['drive_subadmin'],
  restaurant: ['drive_subadmin'],
};

function naTozsamosc(o: OdpowiedzTozsamosci['tozsamosc']): Identity {
  const role = ROLA_NA_ROLE_ZRODEL[o.rola] ?? [];
  return {
    subject: o.email,
    realm: 'cc',
    roles: [o.rola],
    permissions: makePermissionSet(permissionsForRoles([...role])),
  };
}

export function createCcAuthProvider(client: ApiClient): AuthProvider {
  return {
    realm: 'cc',
    label: 'Control Center',
    required: true,

    async signIn(credentials: Credentials): Promise<Identity> {
      // `sessionRequest`, nie `request`: klient ma bezpiecznik odcinający KAŻDĄ metodę
      // zapisującą, a logowanie jest POST-em. Wyjątek jest imienny i ograniczony do dwóch
      // ścieżek sesji — patrz SESSION_PATHS. Pierwsza wersja tego pliku wołała `request`
      // i logowanie odbijało się od własnego bezpiecznika, zanim doszło do backendu.
      const res = await client.sessionRequest<OdpowiedzTozsamosci>('crm', '/api/cc/auth/login', {
        method: 'POST',
        body: { email: credentials.login, haslo: credentials.password },
      });
      return naTozsamosc(res.tozsamosc);
    },

    async signOut(): Promise<void> {
      // Wylogowanie nie może się wywrócić na błędzie sieci: użytkownik kliknął „wyloguj"
      // i ma zostać wylogowany także wtedy, gdy backend akurat nie odpowiada. Ciasteczko
      // i tak przestaje być użyteczne, bo stan aplikacji wraca do anonimowego.
      await client.sessionRequest('crm', '/api/cc/auth/logout', { method: 'POST', body: {} }).catch(() => undefined);
    },

    /**
     * Odtworzenie sesji przy starcie. Ciasteczko jest httpOnly, więc front NIE MA jak sprawdzić,
     * czy sesja istnieje — musi zapytać. Brak sesji to normalny stan (401), nie awaria.
     *
     * Rozróżnienie ma znaczenie: każdy inny błąd propagujemy, bo „nie wiadomo, czy jesteś
     * zalogowany" i „nie jesteś zalogowany" to dwa różne zdania, a mylenie ich kończy się
     * formularzem logowania wyświetlonym przy chwilowej niedostępności backendu.
     */
    async restore(): Promise<Identity | null> {
      try {
        const res = await client.request<OdpowiedzTozsamosci>('crm', '/api/cc/auth/me');
        return naTozsamosc(res.tozsamosc);
      } catch (err) {
        const status = (err as { status?: number })?.status;
        if (status === 401) return null;
        throw err;
      }
    },
  };
}
